import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarPlus, LogOut } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { hasPermission } from "@/lib/permissions";
import { ROUTES } from "@/lib/routes";
import { useAuthStore } from "@/stores/useAuthStore";
import { useNavStore } from "@/stores/useNavStore";
import { canDownloadSupportPlatform } from "@/features/support-platform/access";

import { IntakeNavItem } from "@/components/nav/components/IntakeNavItem";
import { NavMenuItem } from "@/components/nav/components/NavMenuItem";
import { SupportPlatformNavItem } from "@/components/nav/components/SupportPlatformNavItem";
import { SETTINGS_ITEMS, WORKSPACE_ITEMS } from "@/components/nav/constants/navItems";
import { initials, isPathActive, visibleFor } from "@/components/nav/utils/navUtils";
import type { ActionItem } from "@/components/nav/types/nav";

export function Nav() {
  const roles = useAuthStore((s) => s.roles);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const setCalendarSyncOpen = useNavStore((s) => s.setCalendarSyncOpen);
  const { logout: auth0Logout, user: auth0User } = useAuth0();
  const [accountOpen, setAccountOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const workspace = visibleFor(WORKSPACE_ITEMS, roles);
  const settings = visibleFor(SETTINGS_ITEMS, roles);
  const canSyncCalendar = hasPermission(roles, "view_schedule");
  const canDownloadPlatforms = canDownloadSupportPlatform(roles, auth0User?.email);

  const syncAction: ActionItem = {
    label: "Sync to Calendar",
    icon: CalendarPlus,
    action: () => setCalendarSyncOpen(true),
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Workspace</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {workspace.map((item) => (
                <NavMenuItem
                  key={item.to}
                  item={item}
                  isActive={isPathActive(pathname, item.to)}
                  onNavigate={navigate}
                />
              ))}
              <IntakeNavItem
                roles={roles}
                isActive={isPathActive(pathname, ROUTES.INTAKE)}
                onNavigate={navigate}
              />
              {canSyncCalendar ? (
                <SidebarMenuItem>
                  <SidebarMenuButton tooltip={syncAction.label} onClick={syncAction.action}>
                    <syncAction.icon />
                    <span>{syncAction.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : null}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {settings.length > 0 ? (
          <SidebarGroup>
            <SidebarGroupLabel>Settings</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {settings.map((item) => (
                  <NavMenuItem
                    key={item.to}
                    item={item}
                    isActive={isPathActive(pathname, item.to)}
                    onNavigate={navigate}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {canDownloadPlatforms ? (
          <SidebarGroup>
            <SidebarGroupLabel>Platforms</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SupportPlatformNavItem />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border/60">
        {auth0User ? (
          <div className="flex justify-center py-1">
            <Popover open={accountOpen} onOpenChange={setAccountOpen}>
              <Tooltip
                open={tooltipOpen && !accountOpen}
                onOpenChange={setTooltipOpen}
              >
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={auth0User.name ?? auth0User.email ?? "Account"}
                      className="rounded-full outline-none ring-offset-sidebar transition-shadow focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <Avatar size="sm">
                        <AvatarImage src={auth0User.picture} alt="" />
                        <AvatarFallback className="text-[10px]">
                          {initials(auth0User.name ?? auth0User.email ?? "?")}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {auth0User.name ?? auth0User.email}
                </TooltipContent>
              </Tooltip>
              <PopoverContent side="right" align="end" className="w-56 p-2">
                <div className="px-2 pb-2 border-b border-border/60">
                  <div className="text-sm font-medium truncate leading-tight">
                    {auth0User.name ?? auth0User.email}
                  </div>
                  {auth0User.name && auth0User.email ? (
                    <div className="text-xs text-muted-foreground truncate leading-tight">
                      {auth0User.email}
                    </div>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start mt-1"
                  onClick={() =>
                    auth0Logout({ logoutParams: { returnTo: window.location.origin } })
                  }
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </Button>
              </PopoverContent>
            </Popover>
          </div>
        ) : null}
      </SidebarFooter>
    </Sidebar>
  );
}
